import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  MessageItem,
  WsMessageEvent,
  WsTypingEvent,
  WsCsatRequestEvent,
  ConversationDetail,
} from './types';

interface TypingUser {
  userId: number;
  userName: string;
  userType: 'customer' | 'admin';
}

interface SupportChatContextValue {
  isConnected: boolean;
  joinConversation: (conversationId: number) => void;
  leaveConversation: (conversationId: number) => void;
  sendTypingIndicator: (conversationId: number, isTyping: boolean) => void;
  markMessagesRead: (conversationId: number, messageIds?: number[]) => void;
  typingUsers: Record<number, TypingUser[]>;
  csatRequested: number | null;
  clearCsatRequest: () => void;
}

const SupportChatContext = createContext<SupportChatContextValue | null>(null);

export function useSupportChatContext() {
  const context = useContext(SupportChatContext);
  if (!context) {
    throw new Error('useSupportChatContext must be used within SupportChatProvider');
  }
  return context;
}

interface SupportChatProviderProps {
  children: React.ReactNode;
}

export function SupportChatProvider({ children }: SupportChatProviderProps) {
  const { session, user } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Record<number, TypingUser[]>>({});
  const [csatRequested, setCsatRequested] = useState<number | null>(null);

  // Get token from session or localStorage
  const token = session?.token || localStorage.getItem('auth_token');

  // Connect to WebSocket
  useEffect(() => {
    if (!token) return;

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    const socket = io(`${apiUrl}/support-chat`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      // Refresh active conversation status on connect to ensure we have latest data
      queryClient.invalidateQueries({ queryKey: ['customer-support-active-conversation'] });
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversations'] });
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    socket.on('connect_error', () => {
      setIsConnected(false);
    });

    // Handle new messages (from conversation room or user room)
    socket.on('support:message', (event: WsMessageEvent) => {
      // Ensure conversationId is a number (WebSocket might send it as string)
      const conversationId = typeof event.conversationId === 'string' 
        ? parseInt(event.conversationId, 10) 
        : event.conversationId;
      
      // Force refetch all conversation queries to get the new message
      // Refetch both the specific conversation AND the active conversation
      // The widget might be using either one depending on how the user navigated
      queryClient.refetchQueries({ 
        queryKey: ['customer-support-conversation', conversationId],
      });
      queryClient.refetchQueries({ 
        queryKey: ['customer-support-active-conversation'],
      });
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversations'] });
    });

    // Handle new message notification (when not in conversation room)
    socket.on('support:new-message', (event: { conversationId: number | string; preview: string }) => {
      const conversationId = typeof event.conversationId === 'string' 
        ? parseInt(event.conversationId, 10) 
        : event.conversationId;
      
      // Refetch to get the full message
      queryClient.refetchQueries({ queryKey: ['customer-support-conversation', conversationId] });
      queryClient.refetchQueries({ queryKey: ['customer-support-active-conversation'] });
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversations'] });
    });

    // Handle typing indicators
    socket.on('support:typing', (event: WsTypingEvent) => {
      setTypingUsers((prev) => {
        const conversationTyping = prev[event.conversationId] || [];

        if (event.isTyping) {
          // Add user if not already typing
          const exists = conversationTyping.some((u) => u.userId === event.userId);
          if (!exists) {
            return {
              ...prev,
              [event.conversationId]: [
                ...conversationTyping,
                { userId: event.userId, userName: event.userName, userType: event.userType },
              ],
            };
          }
        } else {
          // Remove user from typing
          return {
            ...prev,
            [event.conversationId]: conversationTyping.filter((u) => u.userId !== event.userId),
          };
        }
        return prev;
      });
      
      // Auto-clear typing after 5 seconds (in case stop event is missed)
      if (event.isTyping) {
        setTimeout(() => {
          setTypingUsers((prev) => ({
            ...prev,
            [event.conversationId]: (prev[event.conversationId] || []).filter(
              (u) => u.userId !== event.userId
            ),
          }));
        }, 5000);
      }
    });

    // Handle CSAT request
    socket.on('support:csat-request', (event: WsCsatRequestEvent) => {
      setCsatRequested(event.conversationId);
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversation', event.conversationId] });
    });

    // Handle conversation status changes (e.g., closed by admin)
    socket.on('support:conversation-status-changed', (event: { conversationId: number; status: string }) => {
      // Invalidate all relevant queries to reflect the status change
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversation', event.conversationId] });
      queryClient.invalidateQueries({ queryKey: ['customer-support-active-conversation'] });
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversations'] });
    });

    // Handle message deletion (when admin deletes a message)
    socket.on('support:message-deleted', (event: { conversationId: number; messageId: number }) => {
      const conversationId = typeof event.conversationId === 'string' 
        ? parseInt(event.conversationId as unknown as string, 10) 
        : event.conversationId;
      
      // Refetch to remove the deleted message
      queryClient.refetchQueries({ queryKey: ['customer-support-conversation', conversationId] });
      queryClient.refetchQueries({ queryKey: ['customer-support-active-conversation'] });
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversations'] });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, queryClient]);

  const joinConversation = useCallback((conversationId: number) => {
    socketRef.current?.emit('support:join-conversation', { conversationId });
  }, []);

  const leaveConversation = useCallback((conversationId: number) => {
    console.log('[SupportChat] Leaving conversation:', conversationId);
    socketRef.current?.emit('support:leave-conversation', { conversationId });
    // Clear typing users for this conversation
    setTypingUsers((prev) => {
      const updated = { ...prev };
      delete updated[conversationId];
      return updated;
    });
  }, []);

  const sendTypingIndicator = useCallback((conversationId: number, isTyping: boolean) => {
    socketRef.current?.emit('support:typing', { conversationId, isTyping });
  }, []);

  const markMessagesRead = useCallback((conversationId: number, messageIds?: number[]) => {
    if (messageIds && messageIds.length > 0) {
      socketRef.current?.emit('support:mark-read', { conversationId, messageIds });
      // Immediately invalidate cache to update unread counts (optimistic update)
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['customer-support-active-conversation'] });
      queryClient.invalidateQueries({ queryKey: ['customer-support-conversations'] });
    }
  }, [queryClient]);

  const clearCsatRequest = useCallback(() => {
    setCsatRequested(null);
  }, []);

  const value: SupportChatContextValue = {
    isConnected,
    joinConversation,
    leaveConversation,
    sendTypingIndicator,
    markMessagesRead,
    typingUsers,
    csatRequested,
    clearCsatRequest,
  };

  return <SupportChatContext.Provider value={value}>{children}</SupportChatContext.Provider>;
}
